import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { companiesAPI } from '../services/api'
import type { Company } from '../types'

export default function CompanySettings() {
  const { t } = useTranslation()
  const [company, setCompany] = useState<Company | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    provider_name: '',
    provider_nif: '',
    provider_address: '',
    default_locale: 'es',
    contract_number_prefix: 'CTR-',
  })

  useEffect(() => {
    companiesAPI.current()
      .then((resp) => {
        setCompany(resp.data)
        const s = resp.data.settings
        if (s) {
          setForm({
            provider_name: s.provider_name || '',
            provider_nif: s.provider_nif || '',
            provider_address: s.provider_address || '',
            default_locale: s.default_locale || 'es',
            contract_number_prefix: s.contract_number_prefix || 'CTR-',
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await companiesAPI.updateSettings(form)
      alert('Configuración guardada correctamente')
    } catch {
      alert('Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleCertUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('certificate', file)
    try {
      await companiesAPI.uploadCertificate(fd)
      alert('Certificado subido correctamente')
    } catch {
      alert('Error al subir certificado')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-gear me-2"></i>{t('settings.title')}</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
          {t('common.save')}
        </button>
      </div>

      <div className="row g-3">
        {/* Provider info */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">{t('settings.provider')}</div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Nombre del proveedor</label>
                <input className="form-control" value={form.provider_name}
                  onChange={(e) => setForm({ ...form, provider_name: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">NIF / CIF</label>
                <input className="form-control" value={form.provider_nif}
                  onChange={(e) => setForm({ ...form, provider_nif: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Dirección</label>
                <textarea className="form-control" rows={3} value={form.provider_address}
                  onChange={(e) => setForm({ ...form, provider_address: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Idioma por defecto</label>
                <select className="form-select" value={form.default_locale}
                  onChange={(e) => setForm({ ...form, default_locale: e.target.value })}>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Prefijo de numeración</label>
                <input className="form-control" value={form.contract_number_prefix}
                  onChange={(e) => setForm({ ...form, contract_number_prefix: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        {/* Certificate */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">{t('settings.certificate')}</div>
            <div className="card-body">
              {company?.settings?.has_certificate ? (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  Certificado cargado
                  <br />
                  <small className="text-muted">
                    Huella: {company.settings.certificate_thumbprint}
                  </small>
                </div>
              ) : (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No hay certificado de firma configurado
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Subir certificado (.p12 / .pfx)</label>
                <input className="form-control" type="file" accept=".p12,.pfx" onChange={handleCertUpload} />
              </div>
              <div className="text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                El certificado se utiliza para firmar digitalmente los contratos (PAdES).
                Debe ser un archivo PKCS#12 válido.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
